<template>
  <div class="page-table scrollable only-y call-history-plot-view" v-loading="loading">
    <div class="table-box page-header card-base card-shadow--medium">
      <div class="flex align-center mb-10 justify-space-between">
        <h2 class="mb-0 mt-0">
          <v-svg sprite="report" class="voxo-blue"></v-svg>
          Billing Activity
        </h2>
        <el-tag type="success">
          <h2 class="mb-0 mt-0 mr-10">
            Balance: ${{new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(currentBalance)}}
          </h2>
        </el-tag>
      </div>
      <div id="billing-plot" style="height: 385px; width: 100%"></div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import echarts from "echarts";

export default {
  name: "BillingPlot",
  props: {
    maxTicks: 1,
    plotData: {required: true},
    loading: {required: true},
    currentBalance: {required: true},
    colors: {
      default: () => ["#13ce66", "#0F85FF", "#ff8f02", "#f79ca6", '#c485db'],
    },
  },
  data() {
    return {
      advancedActive: false,
      modalVisible: false,
      primaryLegend: [],
      chart: null,
      xInterval: 0,
      page: 1,
      pages: 0,
      limit: 3,
      total: 0,
      plotMetrics: ["total", "extensions", "sipTrunks", "faxes", "tollFree"]
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    colorTheme() {
      return this.$store.state.layout.theme;
    },
  },
  watch: {
    plotData(){
      if(this.plotData){
        this.doPreset();
      }
    }
  },
  methods: {

    doPreset() {
      this.buildChart();
    },

    buildX() {
      const dateKeys = this.plotData.slice(0, 12).map(item => moment(item.date).format('MMM YYYY')).reverse();
      this.xInterval = 0;

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        data: dateKeys,
        boundaryGap: true,
        axisLabel: {
          interval: this.xInterval,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: axisColor,
          },
        },
        axisTick: {
          interval: this.xInterval,
        },
      };
    },
    buildY1() {
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        name: "Count",
        position: "left",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      };
    },
    buildY2() {
      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      return {
        name: "Cost (USD)",
        position: "right",
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      };
    },
    buildPrimary(xAxis) {
      this.primaryLegend = [];
      const lineData = [...this.plotData].slice(0,12).reverse();

      const nameMap = {
        total: "Cost",
        extensions: "Extensions",
        sipTrunks: "SIP Trunks",
        faxes: "Faxes",
        tollFree: "Toll Free Minutes"
      };

      let dataGroups = {};
      for (let i = 0; i < this.plotMetrics.length; i++) {
        dataGroups[nameMap[this.plotMetrics[i]]] = [];
      }

      for(let i = 0; i < lineData.length; i++){
        const currentBill = lineData[i].data;
        for(let k = 0; k < this.plotMetrics.length; k++){
          const currentMetric = this.plotMetrics[k];
          dataGroups[nameMap[currentMetric]].push(currentBill[currentMetric]);
        }
      }

      let series = [];
      let count = 0;
      //make a yIndex we can adjust with our ternary below
      let yIndex = 0;
      let type = "bar";
      for (let group in dataGroups) {
        if (dataGroups.hasOwnProperty(group)) {
          //ternary to determine if yAxisIndex should be 1 or 0
          yIndex = group === 'Cost' ? 1 : 0;
          type = group === 'Cost' && xAxis.length > 1 ? 'line'  : 'bar';
          series.push({
            name: `${group}`,
            type: type,
            symbol: "circle",
            smooth: true,
            showSymbol: true,
            yAxisIndex: yIndex,
            symbolRepeat: true,
            symbolSize: 3,
            symbolMargin: 1,
            barMaxWidth: 5,
            z: -10,
            lineStyle: {
              color: this.colors[count],
            },
            itemStyle: {
              color: this.colors[count],
              borderColor: this.colors[count],
              borderWidth: 3,
            },
            data: dataGroups[group],
          });
          this.primaryLegend.push({ name: `${group}` });
          count++;
        }
      }
      return series;
    },
    buildChart() {
      if (this.chart) {
        this.chart.dispose();
      }

      this.chart = echarts.init(this.chartElement);

      const xAxis = this.buildX();
      const y1 = this.buildY1();
      const y2 = this.buildY2();
      const yAxis = [y1, y2];

      const seriesData = this.buildPrimary(xAxis.data);

      const axisColor =
        this.colorTheme === "theme-dark"
          ? "rgba(255,255,255,0.5)"
          : "rgba(0,0,0, 0.5)";

      this.chart.setOption({
        grid: {},
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
        },
        xAxis,
        yAxis,
        series: seriesData,
        legend: {
          show: true,
          type: "scroll",
          orient: "horizontal",
          icon: "rectangle",
          itemWidth: 15,
          itemHeight: 3,
          data: this.primaryLegend,
          textStyle: {
            color: axisColor,
          },
        },
      });
    }
  },
  mounted() {
    this.chartElement = document.getElementById("billing-plot");
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
};
</script>
